'use client'

import { usePathname } from 'next/navigation'
import { ArrowRight, Check, ChevronRight, Quote } from 'lucide-react'
import { motion } from 'framer-motion'

function TemplateTile({ t, i, selected, onSelect }: { t: any, i: number, selected: boolean, onSelect: () => void }) {
  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.03, duration: 0.25 }}
      onClick={onSelect}
      className="group flex flex-col gap-2 text-left cursor-pointer"
    >
      <div
        className="aspect-4/3 w-full rounded-xl relative overflow-hidden transition-all"
        style={selected
          ? { boxShadow: '0 0 0 2px white, 0 0 0 4px rgba(0,0,0,0.6)' }
          : { border: '1px solid rgba(255,255,255,0.13)', background: 'rgba(255,255,255,0.04)' }}
      >
        <img src={formatImageURL(t.thumbnail)} alt={t.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        {selected && (
          <div className="absolute bottom-1.5 right-1.5 w-5 h-5 rounded-full bg-white inline-flex items-center justify-center">
            <Check className="h-3 w-3" style={{ color: '#0a0a0c' }} strokeWidth={3} />
          </div>
        )}
      </div>
      <div className="text-[12px] font-medium truncate px-0.5" style={{ color: selected ? 'white' : 'rgba(255,255,255,0.6)' }}>
        {t.name}
      </div>
    </motion.button>
  )
}
import { useGiftCardForm } from '@/lib/commerce/hooks'
import { formatImageURL, formatPrice } from '@/utils/util'
import { GIFT_CARD_MIN_AMOUNT, GIFT_CARD_MAX_AMOUNT, GIFT_CARD_PRESETS } from '@/utils/giftCard'
import { navigateUrl } from '@/utils/client-request'

const inputStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.3)',
  border: '1px solid rgba(255,255,255,0.13)',
  color: 'white',
}

const MESSAGE_MAX = 240

function GiftCardPreview({ template, amount, recipient, message, vendorData }: { template: any, amount: string, recipient: string, message: string, vendorData: any }) {
  const value = amount && parseInt(amount, 10) > 0 ? parseInt(amount, 10) : null
  const thumbnail = template?.thumbnail ? formatImageURL(template.thumbnail) : null

  return (
    <div
      className="relative aspect-[1.586/1] rounded-3xl overflow-hidden"
      style={{
        background: thumbnail ? '#08080a' : 'linear-gradient(135deg, #1a1a20 0%, #08080a 100%)',
        boxShadow: '0 30px 80px -20px rgba(0,0,0,0.6)',
        border: '1px solid rgba(255,255,255,0.07)',
      }}
    >
      {thumbnail && (
        <>
          <img src={thumbnail} alt={template?.name || ''} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.7) 100%)' }} />
        </>
      )}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 40%, rgba(0,0,0,0.15) 100%)' }} />

      <div className="absolute inset-0 flex flex-col justify-between p-5 sm:p-7 text-white">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {vendorData?.logo && (
              <img src={formatImageURL(vendorData.logo)} alt={vendorData?.name || ''} className="size-10 rounded-lg object-cover bg-white/90 p-0.5 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em]" style={{ color: 'rgba(255,255,255,0.7)' }}>Бэлгийн карт</div>
              <div className="mt-1 text-[18px] sm:text-[24px] font-bold tracking-[-0.02em] truncate" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                {vendorData?.name || 'shop.mn'}
              </div>
            </div>
          </div>
          {template?.name && (
            <div className="text-right shrink-0">
              <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: 'rgba(255,255,255,0.7)' }}>{template.name}</div>
            </div>
          )}
        </div>

        <div className="min-w-0">
          {value ? (
            <div className="font-semibold tabular-nums tracking-[-0.025em] leading-none text-[44px] sm:text-[56px] lg:text-[64px]" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
              ₮ {value.toLocaleString('en-US')}
            </div>
          ) : (
            <div className="text-[13px]" style={{ color: 'rgba(255,255,255,0.4)' }}>Дүнгээ сонгоно уу</div>
          )}
          {message && (
            <div className="mt-3 flex items-start gap-2 max-w-md">
              <Quote className="h-3.5 w-3.5 mt-1 shrink-0" style={{ color: 'rgba(255,255,255,0.5)' }} />
              <div className="text-[12px] italic leading-relaxed line-clamp-2" style={{ color: 'rgba(255,255,255,0.85)' }}>{message}</div>
            </div>
          )}
        </div>

        <div className="text-[11px]">
          <div className="uppercase tracking-[0.2em]" style={{ color: 'rgba(255,255,255,0.7)' }}>Хүлээн авагч</div>
          <div className="mt-1 font-medium text-[14px] sm:text-[16px]" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
            {recipient || 'Хүлээн авагчийн нэр'}
          </div>
        </div>
      </div>
    </div>
  )
}

function Step({ n, title, desc }: { n: string, title: string, desc?: string }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-[11px] font-mono tabular-nums tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.4)' }}>{n}</span>
      <div>
        <h2 className="text-[18px] font-semibold tracking-[-0.01em] text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{title}</h2>
        {desc && <p className="mt-1 text-[13px]" style={{ color: 'rgba(255,255,255,0.55)' }}>{desc}</p>}
      </div>
    </div>
  )
}

function FieldLabel({ children, required }: { children: React.ReactNode, required?: boolean }) {
  return (
    <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
      {children}
      {required && <span className="ml-1" style={{ color: '#fda4af' }}>*</span>}
    </label>
  )
}

export default function GiftCard({ templates, vendorData }: { templates: any, vendorData: any }) {
  const pathname = usePathname()
  const f = useGiftCardForm({ templates, vendorData, totalSteps: 1 })

  const vendorTemplates = (templates || []).filter((t: any) => t.vendorId)
  const globalTemplates = (templates || []).filter((t: any) => !t.vendorId)

  if (!f.giftCardTemplateId) {
    const first = vendorTemplates[0] || globalTemplates[0]
    if (first) f.setGiftCardTemplateId(first.id)
  }

  const amountNum = f.amount ? parseInt(f.amount, 10) : 0
  const total = amountNum

  const scheduled = f.deliveryDate ? 'later' : 'now'
  const setScheduled = (id: 'now' | 'later') => {
    if (id === 'now') f.setDeliveryDate(null)
    else if (!f.deliveryDate) f.setDeliveryDate(new Date(Date.now()))
  }

  const messageLen = f.message?.length || 0

  return (
    <div>
      <div className="cont pt-10 pb-10" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="hidden md:flex items-center gap-1.5 text-[12px] mb-8" style={{ color: 'rgba(255,255,255,0.55)' }}>
          <a href={navigateUrl('/', pathname)} className="hover:text-white transition-colors">Нүүр</a>
          <ChevronRight className="h-3 w-3" style={{ opacity: 0.5 }} />
          <span className="text-white font-medium">Бэлгийн карт</span>
        </div>

        <h1
          className="mt-3 font-semibold tracking-[-0.025em] leading-[0.95] text-white max-w-3xl"
          style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(36px, 7vw, 64px)' }}
        >
          Хэлэхийг хүссэн зүйлээ<br />картаар бэлэглэ.
        </h1>
        <p className="mt-5 text-[15px] leading-7 max-w-xl" style={{ color: 'rgba(255,255,255,0.6)' }}>
          Бэлгийн картаа имэйлээр нь шууд, эсвэл хүссэн өдрөө хүргүүлээрэй.
        </p>
      </div>

      <div className="cont pt-10 pb-16">
        <div className="grid gap-8 lg:gap-10 grid-cols-1 lg:grid-cols-[1.1fr_1fr] items-start">
          <aside className="order-1 lg:sticky lg:top-32 self-start w-full min-w-0">
            <GiftCardPreview
              template={f.selectedTemplate}
              amount={f.amount}
              recipient={f.fullName}
              message={f.message}
              vendorData={vendorData}
            />
            <div className="mt-5 text-[12px] flex items-center justify-between" style={{ color: 'rgba(255,255,255,0.55)' }}>
              <span>Урьдчилан харах · {f.selectedTemplate?.name || '—'}</span>
              <span className="tabular-nums">{amountNum ? `${amountNum.toLocaleString('en-US')}₮` : '—'}</span>
            </div>
          </aside>

          <div className="order-2 lg:order-2 space-y-7 min-w-0">
            <section>
              <Step n="01" title="Картын дизайныг сонгоно уу" desc="Зургуудаас тохирохыг сонгоход зүүн талд урьдчилсан байдал шинэчлэгдэнэ." />

              {vendorTemplates.length > 0 && (
                <div className="mt-5">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-3" style={{ color: '#a8a8b3' }}>
                    {vendorData?.name ? `${vendorData.name}-н дизайн` : 'Дэлгүүрийн дизайн'}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {vendorTemplates.map((t: any, i: number) => (
                      <TemplateTile key={t.id} t={t} i={i} selected={f.giftCardTemplateId === t.id} onSelect={() => f.setGiftCardTemplateId(t.id)} />
                    ))}
                  </div>
                </div>
              )}

              {globalTemplates.length > 0 && (
                <div className="mt-6">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-3" style={{ color: '#a8a8b3' }}>
                    {vendorTemplates.length > 0 ? 'Бэлэн дизайн' : 'Дизайн сонгох'}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {globalTemplates.map((t: any, i: number) => (
                      <TemplateTile key={t.id} t={t} i={i} selected={f.giftCardTemplateId === t.id} onSelect={() => f.setGiftCardTemplateId(t.id)} />
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section>
              <Step n="02" title="Үнийн дүнг тохируулна уу" desc={`${formatPrice(GIFT_CARD_MIN_AMOUNT)}-аас ${formatPrice(GIFT_CARD_MAX_AMOUNT)}-н хооронд.`} />
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {GIFT_CARD_PRESETS.map(a => {
                  const sel = a === amountNum
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => f.setAmount(String(a))}
                      className="h-14 rounded-xl text-[14px] font-semibold tabular-nums transition-colors cursor-pointer"
                      style={sel
                        ? { background: 'white', color: '#0a0a0c', border: '1px solid white' }
                        : { background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.13)', color: 'white' }}
                    >
                      ₮ {a.toLocaleString('en-US')}
                    </button>
                  )
                })}
              </div>
              <div className="mt-3">
                <FieldLabel>Өөр дүн</FieldLabel>
                <div className="relative">
                  <input
                    type="number"
                    value={f.amount}
                    onChange={(e: any) => f.setAmount(e.target.value)}
                    placeholder={`${formatPrice(GIFT_CARD_MIN_AMOUNT)} - ${formatPrice(GIFT_CARD_MAX_AMOUNT)}`}
                    className="w-full h-12 px-5 pr-10 rounded-full text-[13.5px] tabular-nums outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                  <span className="absolute right-5 top-1/2 -translate-y-1/2 text-[13.5px]" style={{ color: 'rgba(255,255,255,0.4)' }}>₮</span>
                </div>
              </div>
            </section>

            <section>
              <Step n="03" title="Хүлээн авагчийн мэдээлэл" desc="Картыг өгөгдсөн огноонд автоматаар илгээнэ." />
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Нэр</FieldLabel>
                  <input
                    value={f.fullName}
                    onChange={(e: any) => f.setFullName(e.target.value)}
                    placeholder="Хүлээн авагчийн нэр"
                    className="w-full h-12 rounded-full px-5 text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <FieldLabel>И-мэйл</FieldLabel>
                  <input
                    type="email"
                    value={f.email}
                    onChange={(e: any) => f.setEmail(e.target.value)}
                    placeholder="mail@shop.mn"
                    className="w-full h-12 rounded-full px-5 text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                </div>
              </div>
              {f.isGuest && (
                <div className="mt-3">
                  <FieldLabel>Өөрийн и-мэйл</FieldLabel>
                  <input
                    type="email"
                    value={f.senderEmail}
                    onChange={(e: any) => f.setSenderEmail(e.target.value)}
                    placeholder="me@mail.mn"
                    className="w-full h-12 rounded-full px-5 text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                </div>
              )}

              <button
                type="button"
                onClick={() => f.setSendCopy(!f.sendCopy)}
                className="mt-4 w-full text-left rounded-xl p-4 cursor-pointer transition-colors"
                style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.13)' }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="shrink-0 mt-0.5 h-5 w-5 rounded-md inline-flex items-center justify-center transition-colors"
                    style={f.sendCopy
                      ? { background: 'white', color: '#0a0a0c' }
                      : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.2)' }}
                  >
                    {f.sendCopy && <Check className="h-3 w-3" strokeWidth={3} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-white">Хуулбар авах</div>
                    <div className="mt-0.5 text-[11.5px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>
                      Картыг илгээх үед таны и-мэйлд картын хуулбар очно.
                    </div>
                  </div>
                </div>
              </button>
            </section>

            <section>
              <Step n="04" title="Хувийн захиа (заавал биш)" />
              <div className="mt-4">
                <textarea
                  rows={3}
                  maxLength={MESSAGE_MAX}
                  value={f.message}
                  onChange={(e: any) => f.setMessage(e.target.value)}
                  placeholder="Төрсөн өдрийн мэнд! Сонгосон зүйлээ цуглуулаарай."
                  className="w-full rounded-2xl px-5 py-4 text-[13.5px] outline-none focus:ring-0 resize-none leading-6 placeholder:text-white/30 scrollbar-dark"
                  style={inputStyle}
                />
                <div className="mt-1 flex justify-between text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  <span>{MESSAGE_MAX} тэмдэгт хүртэл</span>
                  <span className="tabular-nums">{messageLen} / {MESSAGE_MAX}</span>
                </div>
              </div>
            </section>

            <section>
              <Step n="05" title="Хүргэлтийн цаг" />
              <div className="mt-4 grid grid-cols-2 gap-2">
                {[
                  { id: 'now' as const, t: 'Шууд илгээх', s: 'Худалдан авмагц' },
                  { id: 'later' as const, t: 'Өдөр товлох', s: 'Цаг сонгох' },
                ].map(o => {
                  const active = o.id === scheduled
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setScheduled(o.id)}
                      className="text-left p-4 rounded-xl cursor-pointer transition-colors"
                      style={active
                        ? { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.3)' }
                        : { background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.13)' }}
                    >
                      <div className="inline-flex items-center gap-2">
                        <span
                          className="w-4 h-4 rounded-full inline-flex items-center justify-center"
                          style={{ border: `1.5px solid ${active ? 'white' : 'rgba(255,255,255,0.3)'}`, background: active ? 'white' : 'transparent' }}
                        >
                          {active && <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#0a0a0c' }} />}
                        </span>
                        <span className="text-[13px] font-medium text-white">{o.t}</span>
                      </div>
                      <div className="mt-1.5 text-[11px] pl-6" style={{ color: 'rgba(255,255,255,0.55)' }}>{o.s}</div>
                    </button>
                  )
                })}
              </div>
              {scheduled === 'later' && (
                <div className="mt-3">
                  <FieldLabel>Илгээх огноо</FieldLabel>
                  <input
                    type="datetime-local"
                    value={f.deliveryDate ? new Date(f.deliveryDate.getTime() - f.deliveryDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                    onChange={(e: any) => f.setDeliveryDate(e.target.value ? new Date(e.target.value) : null)}
                    className="w-full h-12 px-5 rounded-full text-[13.5px] outline-none focus:ring-0 cursor-pointer"
                    style={{ ...inputStyle, colorScheme: 'dark' }}
                  />
                </div>
              )}
            </section>

            <section
              className="rounded-2xl p-6"
              style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="space-y-2.5 text-[13px]">
                <div className="flex justify-between">
                  <span style={{ color: 'rgba(255,255,255,0.55)' }}>Картын дүн</span>
                  <span className="tabular-nums text-white">{formatPrice(amountNum)}</span>
                </div>
              </div>
              <div className="mt-4 pt-4 flex items-baseline justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <span className="text-[13px] text-white">Нийт төлбөр</span>
                <span className="text-[20px] font-semibold tabular-nums text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{formatPrice(total)}</span>
              </div>
              <button
                type="button"
                onClick={f.submit}
                disabled={f.loading}
                className="mt-5 w-full h-14 rounded-full text-[14px] font-medium inline-flex items-center justify-center gap-2 cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ background: 'white', color: '#0a0a0c' }}
              >
                {f.loading ? 'Захиалж байна' : 'Бэлгийн карт авах'} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </section>
          </div>
        </div>
      </div>

      <div className="cont pb-20">
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-3" style={{ color: '#a8a8b3' }}>Хэрхэн ажилладаг вэ</div>
        <h2 className="font-semibold tracking-[-0.025em] leading-[0.95] text-white mb-10" style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(28px, 4vw, 40px)' }}>
          Гурван алхамтай.
        </h2>
        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-px rounded-2xl overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          {[
            { n: '01', t: 'Дизайн сонгох', d: 'Хүлээн авагчдаа тохирох сэтгэгдлээ илэрхийлсэн загвараа сонгоно.' },
            { n: '02', t: 'Үнэ болон мэдээлэл', d: `${formatPrice(GIFT_CARD_MIN_AMOUNT)}-${formatPrice(GIFT_CARD_MAX_AMOUNT)} хооронд дурын дүн оруулна.` },
            { n: '03', t: 'Илгээх', d: 'Хүлээн авагчийн и-мэйл, хүлээн авах огноог оруулж бэлгээ илгээнэ.' },
          ].map(s => (
            <div key={s.n} className="px-7 py-8" style={{ background: 'rgba(0,0,0,0.25)' }}>
              <div className="text-[13px] font-mono tabular-nums tracking-[0.18em]" style={{ color: '#a8a8b3' }}>{s.n}</div>
              <h3 className="mt-4 text-[20px] font-semibold tracking-[-0.01em] text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{s.t}</h3>
              <p className="mt-2 text-[13px] leading-6" style={{ color: 'rgba(255,255,255,0.6)' }}>{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
